/**
 * Window Communication — bi-directional messaging between main and detached windows
 *
 * Establishes a communication channel using postMessage and localStorage watchers.
 * Allows portfolio updates, state sync, and event broadcasting across windows.
 */

export type WindowMessageType =
  | 'portfolio:update'
  | 'portfolio:balance'
  | 'portfolio:positions'
  | 'portfolio:orders'
  | 'window:close'
  | 'window:reattach'
  | 'window:requestState'
  | 'window:stateResponse'
  | 'broker:event'
  | 'ws:status';

export interface WindowMessage<T = any> {
  type: WindowMessageType;
  payload: T;
  source: 'main' | 'detached';
  traderId?: string;
  timestamp: number;
}

export interface WindowMessageListener {
  (message: WindowMessage): void;
}

/**
 * Window communication manager for main window
 */
export class WindowCommunicationManager {
  private listeners = new Map<WindowMessageType, Set<WindowMessageListener>>();
  private parentWindow: Window | null = null;
  private childWindows = new Map<string, Window>();

  constructor(private isDetachedWindow: boolean = false) {
    if (isDetachedWindow) {
      // Detached window: listen to parent
      this.parentWindow = window.opener;
      window.addEventListener('message', this.handleMessage.bind(this));
    } else {
      // Main window: listen for messages
      window.addEventListener('message', this.handleMessage.bind(this));
    }
  }

  /**
   * Register a listener for a specific message type
   */
  on(type: WindowMessageType, listener: WindowMessageListener): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);

    // Return unsubscribe function
    return () => {
      const set = this.listeners.get(type);
      if (set) {
        set.delete(listener);
      }
    };
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(event: MessageEvent): void {
    // Only accept messages from trusted origins
    if (event.origin !== window.location.origin) {
      console.warn('[windowCommunication] Rejected message from untrusted origin:', event.origin);
      return;
    }

    const message = event.data as WindowMessage;
    if (!message || !message.type) return;

    // Notify all listeners for this message type
    const listeners = this.listeners.get(message.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          console.error('[windowCommunication] Listener error:', error);
        }
      });
    }
  }

  /**
   * Send message to parent window (for detached windows)
   */
  sendToParent(type: WindowMessageType, payload: any, traderId?: string): void {
    if (this.isDetachedWindow && this.parentWindow) {
      const message: WindowMessage = {
        type,
        payload,
        source: 'detached',
        traderId,
        timestamp: Date.now(),
      };
      this.parentWindow.postMessage(message, window.location.origin);
    }
  }

  /**
   * Send message to a specific child window (for main window)
   */
  sendToChild(traderId: string, type: WindowMessageType, payload: any): void {
    if (this.isDetachedWindow) {
      console.warn('[windowCommunication] Cannot send to child from detached window');
      return;
    }

    const childWindow = this.childWindows.get(traderId);
    if (childWindow && !childWindow.closed) {
      const message: WindowMessage = {
        type,
        payload,
        source: 'main',
        traderId,
        timestamp: Date.now(),
      };
      childWindow.postMessage(message, window.location.origin);
    }
  }

  /**
   * Broadcast message to all child windows (for main window)
   */
  broadcastToChildren(type: WindowMessageType, payload: any): void {
    if (this.isDetachedWindow) {
      console.warn('[windowCommunication] Cannot broadcast from detached window');
      return;
    }

    for (const [traderId, childWindow] of this.childWindows) {
      if (childWindow && !childWindow.closed) {
        const message: WindowMessage = {
          type,
          payload,
          source: 'main',
          traderId,
          timestamp: Date.now(),
        };
        childWindow.postMessage(message, window.location.origin);
      }
    }
  }

  /**
   * Register a detached window reference (for main window)
   */
  registerChildWindow(traderId: string, windowRef: Window): void {
    if (!this.isDetachedWindow) {
      this.childWindows.set(traderId, windowRef);
    }
  }

  /**
   * Unregister a detached window (for main window)
   */
  unregisterChildWindow(traderId: string): void {
    this.childWindows.delete(traderId);
  }

  /**
   * Get number of active child windows
   */
  getChildWindowCount(): number {
    return this.childWindows.size;
  }

  /**
   * Clean up and remove all listeners
   */
  destroy(): void {
    window.removeEventListener('message', this.handleMessage.bind(this));
    this.listeners.clear();
    this.childWindows.clear();
  }
}

/**
 * Portfolio update payload
 */
export interface PortfolioUpdatePayload {
  balance?: {
    accountId: string;
    buyingPower?: number;
    cash?: number;
    totalValue?: number;
  };
  positions?: Array<{
    symbol: string;
    quantity: number;
    marketValue: number;
    unrealizedPnl: number;
  }>;
  orders?: Array<{
    clOrdId: string;
    symbol: string;
    side: 'BUY' | 'SELL';
    quantity: number;
    status: string;
  }>;
  timestamp: number;
}

/**
 * Broker event payload
 */
export interface BrokerEventPayload {
  eventType: string;
  data: any;
  timestamp: number;
}

/**
 * WebSocket status payload
 */
export interface WsStatusPayload {
  status: 'open' | 'closed' | 'connecting' | 'reconnecting';
  timestamp: number;
}
